import React, { useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { ink, line, surface, radius, font, transition, cardStyle } from 'features/dashboard/tokens'
import { scoreColour, scoreBandFor, RAG } from 'features/dashboard/charts/scoreColour'
import { getShortFloorName } from 'utils/bpData'

const formatPct = v => v === null || v === undefined ? 'No score' : `${Math.round(v)}%`

/**
 * Unit View — focused on a BP, shows ALL its units grouped by floor. Each
 * unit is a small coloured card with its compliance state. Empty units
 * (no task data) get a soft grey treatment so the user can see them
 * existing but distinguish them from tracked ones.
 */
const UnitView = ({ parentBpName, units = [], domainFilter = null, onClearDomainFilter, onOpenUnit }) => {
  const { floorGroups, noFloorUnits } = useMemo(() => {
    const groups = {}
    const noFloor = []
    units.forEach(u => {
      const floor = (u.floorNumbers || [])[0]
      if (floor === null || floor === undefined) {
        noFloor.push(u)
        return
      }
      if (!groups[`${floor}`]) groups[`${floor}`] = []
      groups[`${floor}`].push(u)
    })
    const sorted = Object.entries(groups).sort(([a], [b]) => Number(b) - Number(a))
    return { floorGroups: sorted, noFloorUnits: noFloor }
  }, [units])

  const renderUnitCard = unit => (
    <UnitCard
      key={unit.id}
      $tint={scoreColour(unit.score)}
      $untracked={!unit.hasTaskData}
      $clickable={!!onOpenUnit}
      onClick={onOpenUnit ? () => onOpenUnit(unit.id) : undefined}
      title={onOpenUnit
        ? `Open ${unit.name || unit.plotNumber || 'Unit Passport'}: ${formatPct(unit.score)}`
        : `${unit.name || unit.plotNumber || 'Unit Passport'}: ${formatPct(unit.score)}`}
    >
      <UnitNumber>{unit.plotNumber || unit.name || '—'}</UnitNumber>
      <UnitTinyMeta>
        {unit.score !== null && unit.score !== undefined
          ? `${Math.round(unit.score)}%`
          : '—'}
      </UnitTinyMeta>
      <UnitStatusDot $tint={scoreColour(unit.score)} $untracked={!unit.hasTaskData} />
    </UnitCard>
  )

  const stats = useMemo(() => {
    let low = 0, medium = 0, high = 0, critical = 0, untracked = 0
    units.forEach(u => {
      if (!u.hasTaskData) { untracked++; return }
      const band = scoreBandFor(u.score)
      if (!band) { untracked++; return }
      if (band.level === 'low') low++
      else if (band.level === 'medium') medium++
      else if (band.level === 'high') high++
      else critical++
    })
    return { low, medium, high, critical, untracked }
  }, [units])

  const matrixUnits = useMemo(() => {
    if (!domainFilter) return []
    return units.map(u => {
      const cell = (u.domainBreakdown || []).find(d => d.taskDomainId === domainFilter.id)
      const score = cell?.score ?? null
      return { ...u, domainScore: score, domainOverdue: cell?.overdue ?? 0 }
    })
  }, [units, domainFilter])

  return (
    <>
      <Hero>
        <HeroLeft>
          <Eyebrow>
            {parentBpName ? `${parentBpName} · Unit Passports` : 'Unit Passports'}
            {domainFilter && (
              <>
                {' '}·{' '}
                <DomainPill onClick={onClearDomainFilter} title='Clear domain filter'>
                  {domainFilter.name} ✕
                </DomainPill>
              </>
            )}
          </Eyebrow>
          <Title>
            {domainFilter
              ? `${domainFilter.name} across ${units.length} Unit Passport${units.length === 1 ? '' : 's'}`
              : floorGroups.length === 0
                ? `${units.length} Unit Passport${units.length === 1 ? '' : 's'}`
                : `${units.length} Unit Passports across ${floorGroups.length} floor${floorGroups.length === 1 ? '' : 's'}`}
          </Title>
        </HeroLeft>
        <HeroStats>
          <StatBlock $tint={RAG.low.fg}>
            <StatValue>{stats.low}</StatValue>
            <StatLabel>On track</StatLabel>
          </StatBlock>
          <StatBlock $tint={RAG.medium.fg}>
            <StatValue>{stats.medium}</StatValue>
            <StatLabel>Attention</StatLabel>
          </StatBlock>
          <StatBlock $tint={RAG.high.fg}>
            <StatValue>{stats.high}</StatValue>
            <StatLabel>At risk</StatLabel>
          </StatBlock>
          <StatBlock $tint={RAG.critical.fg}>
            <StatValue>{stats.critical}</StatValue>
            <StatLabel>Critical</StatLabel>
          </StatBlock>
          <StatBlock $tint={ink.mute}>
            <StatValue>{stats.untracked}</StatValue>
            <StatLabel>Untracked</StatLabel>
          </StatBlock>
        </HeroStats>
      </Hero>

      {domainFilter ? (
        <Section>
          <SectionHead>
            <SectionEyebrow>{domainFilter.name}. Unit Passport matrix</SectionEyebrow>
          </SectionHead>
          {matrixUnits.length === 0 ? (
            <Empty>No Unit Passports on this Building Passport.</Empty>
          ) : (
            <MatrixTable>
              <MatrixHead>
                <MatrixHeadCell>Unit Passport</MatrixHeadCell>
                <MatrixHeadCell $right>Floor</MatrixHeadCell>
                <MatrixHeadCell $right>Score</MatrixHeadCell>
                <MatrixHeadCell $right>Overdue</MatrixHeadCell>
                <MatrixHeadCell>State</MatrixHeadCell>
              </MatrixHead>
              {matrixUnits.map(u => (
                <MatrixRow key={u.id} $untracked={!u.hasTaskData}>
                  <MatrixCell>
                    <strong>{u.plotNumber || u.name || '—'}</strong>
                  </MatrixCell>
                  <MatrixCell $right>
                    {getShortFloorName((u.floorNumbers || [])[0]) || '—'}
                  </MatrixCell>
                  <MatrixCell $right>
                    <MatrixScore $tint={scoreColour(u.domainScore)}>{formatPct(u.domainScore)}</MatrixScore>
                  </MatrixCell>
                  <MatrixCell $right>
                    {u.domainOverdue > 0 ? <OverdueDot>{u.domainOverdue}</OverdueDot> : '—'}
                  </MatrixCell>
                  <MatrixCell>
                    <MatrixBar>
                      <MatrixBarFill
                        style={{
                          width: `${Math.max(2, u.domainScore ?? 0)}%`,
                          background: scoreColour(u.domainScore),
                        }}
                      />
                    </MatrixBar>
                  </MatrixCell>
                </MatrixRow>
              ))}
            </MatrixTable>
          )}
        </Section>
      ) : (
      <Section>
        <SectionHead>
          <SectionEyebrow>
            {floorGroups.length === 0 ? 'Unit Passport compliance' : 'Floor-by-floor Unit Passport compliance'}
          </SectionEyebrow>
        </SectionHead>
        {units.length === 0 ? (
          <Empty>No Unit Passports on this Building Passport.</Empty>
        ) : floorGroups.length === 0 ? (
          <UnitGrid>
            {units.map(renderUnitCard)}
          </UnitGrid>
        ) : (
          <>
            {floorGroups.map(([floor, floorUnits]) => (
              <FloorBlock key={floor}>
                <FloorLabel>
                  <FloorWord>Floor</FloorWord>
                  <FloorNum>{getShortFloorName(floor)}</FloorNum>
                </FloorLabel>
                <UnitGrid>
                  {floorUnits.map(renderUnitCard)}
                </UnitGrid>
              </FloorBlock>
            ))}
            {noFloorUnits.length > 0 && (
              <FloorBlock>
                <FloorLabel>
                  <FloorWord>No floor assigned</FloorWord>
                </FloorLabel>
                <UnitGrid>
                  {noFloorUnits.map(renderUnitCard)}
                </UnitGrid>
              </FloorBlock>
            )}
          </>
        )}
      </Section>
      )}
    </>
  )
}

const DomainPill = styled.button`
  font: inherit;
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: ${radius.pill};
  background: #E5704D1A;
  color: #E5704D;
  border: 1px solid #E5704D33;
  cursor: pointer;
  &:hover { background: #E5704D33; }
`

const MatrixTable = styled.div`
  display: flex; flex-direction: column;
`

const MatrixHead = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.5fr 0.6fr 0.6fr 1.4fr;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 2px solid ${line.base};
`

const MatrixHeadCell = styled.div`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: ${({ $right }) => $right ? 'right' : 'left'};
`

const MatrixRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.5fr 0.6fr 0.6fr 1.4fr;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid ${surface.wash};
  align-items: center;
  ${({ $untracked }) => $untracked ? 'opacity: 0.55;' : ''}
  &:hover { background: ${surface.wash}; }
`

const MatrixCell = styled.div`
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  text-align: ${({ $right }) => $right ? 'right' : 'left'};
  font-variant-numeric: tabular-nums;
`

const MatrixScore = styled.span`
  font-weight: 800;
  color: ${({ $tint }) => $tint};
`

const OverdueDot = styled.span`
  display: inline-block;
  min-width: 22px;
  padding: 2px 7px;
  border-radius: ${radius.pill};
  background: #b000201A;
  color: #b00020;
  font-size: ${font.caption};
  font-weight: 800;
`

const MatrixBar = styled.div`
  height: 8px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: ${radius.pill};
  overflow: hidden;
`

const MatrixBarFill = styled.div`
  height: 100%;
  border-radius: ${radius.pill};
`

export default UnitView

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Hero = styled.div`
  ${cardStyle}
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 18px;
  padding: 22px 24px;
  margin-bottom: 16px;
  animation: ${fadeUp} 320ms ease both;
`

const HeroLeft = styled.div`
  display: flex; flex-direction: column; gap: 4px;
`

const Eyebrow = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
`

const Title = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.15;
`

const HeroStats = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  align-items: end;
`

const StatBlock = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 8px;
  background: ${({ $tint }) => `${$tint}1A`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  border-radius: ${radius.md};
`

const StatValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

const StatLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Section = styled.section`
  ${cardStyle}
  padding: 18px 22px;
`

const SectionHead = styled.header`
  margin-bottom: 14px;
`

const SectionEyebrow = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Empty = styled.div`
  padding: 32px;
  text-align: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body};
  background: ${({ theme }) => theme.colors.lighterGrey};
  border: 1px dashed ${line.base};
  border-radius: ${radius.md};
`

const FloorBlock = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 16px;
  align-items: start;
  padding: 10px 0;
  border-top: 1px solid ${surface.wash};
  &:first-of-type { border-top: none; padding-top: 0; }
`

const FloorLabel = styled.div`
  display: flex; flex-direction: column; align-items: center;
  padding: 4px 0;
`

const FloorWord = styled.span`
  font-size: 9.5px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`

const FloorNum = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
`

const UnitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(86px, 1fr));
  gap: 6px;
`

const UnitCard = styled.div`
  position: relative;
  padding: 8px 10px;
  background: ${({ $tint, $untracked }) => $untracked ? '#f6f6f8' : `${$tint}1A`};
  border: 1px solid ${({ $tint, $untracked }) => $untracked ? line.base : `${$tint}40`};
  border-radius: ${radius.sm};
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  transition: ${transition};
  &:hover {
    ${({ $clickable, $tint, $untracked }) => $clickable
      ? `border-color: ${$untracked ? line.strong : $tint}; background: ${$untracked ? '#f0f0f4' : `${$tint}26`};`
      : ''}
  }
  ${({ $untracked }) => $untracked ? 'opacity: 0.65;' : ''}
`

const UnitNumber = styled.div`
  font-size: ${font.small};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const UnitTinyMeta = styled.div`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-variant-numeric: tabular-nums;
`

const UnitStatusDot = styled.span`
  position: absolute;
  top: 8px; right: 8px;
  width: 6px; height: 6px;
  border-radius: ${radius.round};
  background: ${({ $tint, $untracked }) => $untracked ? line.strong : $tint};
`
